/*
  Sticky add-to-cart bar for the modern PDP layout.

  Deliberately holds no variant logic of its own. The bar is a remote control
  for the real buy box:

    - its option dropdowns check the matching radio (or set the matching select)
      inside the real variant picker and dispatch `change`, so the theme's
      VariantSelects/VariantRadios does all the actual work;
    - its button forwards clicks to the real add-to-cart button, so
      product-form.js submits with the current variant, quantity and pre-order
      properties;
    - label, disabled state, price and image are mirrored back from the live
      product form.

  It only reveals itself once the buy box has been scrolled past — never while
  the real button is simply still further down the page.
*/
if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.priceSource = document.getElementById(`price-${this.sectionId}`);

        this.button = this.querySelector('[data-sticky-atc-button]');
        this.label = this.querySelector('[data-sticky-atc-label]');
        this.buttonPrice = this.querySelector('[data-sticky-atc-button-price]');
        this.image = this.querySelector('.pdp-sticky-atc__media img');
        this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-atc-option]'));

        if (!this.realButton || !this.button) return;

        this.header = document.querySelector('.section-header');
        this.topFrame = null;

        this.onScroll = this.onScroll.bind(this);
        this.button.addEventListener('click', this.onClick.bind(this));
        this.optionSelects.forEach((select) => {
          select.addEventListener('change', this.onOptionChange.bind(this));
        });

        this.syncButton();
        this.syncPrice();
        this.mirrorUnavailableOptions();
        this.observeButton();
        this.observePrice();
        this.observeVisibility();
        this.observeVariant();
      }

      disconnectedCallback() {
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        this.unsubscribeVariant?.();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.topFrame) cancelAnimationFrame(this.topFrame);
      }

      onClick(event) {
        event.preventDefault();
        if (this.realButton.hasAttribute('disabled')) return;
        this.realButton.click();
      }

      /* --- driving the real variant picker ------------------------------ */

      get variantRadios() {
        return document.querySelector(`variant-radios[data-section="${this.sectionId}"]`);
      }

      get variantSelects() {
        return document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      }

      onOptionChange(event) {
        const index = Number(event.currentTarget.dataset.stickyAtcOption);
        const value = event.currentTarget.value;

        const radios = this.variantRadios;
        if (radios) {
          const fieldset = radios.querySelectorAll('fieldset')[index];
          const input =
            fieldset &&
            Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((el) => el.value === value);
          if (!input || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const selects = this.variantSelects;
        if (!selects) return;
        const select = selects.querySelectorAll('select')[index];
        if (!select || select.value === value) return;
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // The theme marks unavailable option values with `.disabled` at render
      // time; carry that across so the bar cannot offer a dead combination.
      mirrorUnavailableOptions() {
        const radios = this.variantRadios;
        if (!radios) return;

        this.optionSelects.forEach((select, index) => {
          const fieldset = radios.querySelectorAll('fieldset')[index];
          if (!fieldset) return;
          const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

          Array.from(select.options).forEach((option) => {
            const input = inputs.find((el) => el.value === option.value);
            option.disabled = !!input && input.classList.contains('disabled');
          });
        });
      }

      /* --- mirroring back ----------------------------------------------- */

      syncButton() {
        const realLabel = this.realButton.querySelector('span');
        if (realLabel && this.label) this.label.textContent = realLabel.textContent.trim();

        const disabled = this.realButton.hasAttribute('disabled');
        this.button.toggleAttribute('disabled', disabled);
        this.button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      // The button carries the price. Read whichever of the theme's two price
      // branches is showing, and drop a trailing currency code ("$37.50 USD"
      // -> "$37.50") so the button stays short.
      syncPrice() {
        if (!this.buttonPrice || !this.priceSource) return;

        const onSale = this.priceSource.querySelector('.price--on-sale');
        const item = onSale
          ? this.priceSource.querySelector('.price__sale .price-item--sale')
          : this.priceSource.querySelector('.price__regular .price-item--regular');
        if (!item) return;

        const amount = item.textContent.trim().replace(/\s*[A-Za-z]{3}\s*$/, '');
        if (amount) this.buttonPrice.textContent = amount;
      }

      observeButton() {
        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.realButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }

      observePrice() {
        if (!this.priceSource) return;
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.priceSource, { childList: true, subtree: true });
      }

      observeVariant() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.syncImage(event.data.variant);
          this.syncOptions(event.data.variant);
        });
      }

      syncImage(variant) {
        const src = variant?.featured_image?.src;
        if (!this.image || !src) return;

        this.image.removeAttribute('srcset');
        this.image.removeAttribute('sizes');
        this.image.src = `${src}${src.includes('?') ? '&' : '?'}width=208`;
      }

      // Keep the bar's dropdowns honest when the variant is changed from the
      // main picker rather than from here.
      syncOptions(variant) {
        if (!variant?.options) return;
        this.optionSelects.forEach((select, index) => {
          const value = variant.options[index];
          if (value != null && select.value !== value) select.value = value;
        });
      }

      /* --- show / hide -------------------------------------------------- */

      observeVisibility() {
        // Anchor on the whole buttons row so dynamic checkout buttons count as
        // "the add-to-cart area still being on screen".
        const anchor = this.realButton.closest('.product-form__buttons') || this.realButton;

        // Pull the observer's top edge down past the header and the bar itself,
        // so the bar appears only once the buy box has genuinely scrolled up
        // behind it — never while the real button is still below the fold.
        const headerHeight = Math.min(this.header?.offsetHeight || 0, 90);
        this.revealLine = Math.round(headerHeight + (this.offsetHeight || 72));

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const rootTop = entry.rootBounds ? entry.rootBounds.top : this.revealLine;
              const scrolledPast = entry.boundingClientRect.bottom <= rootTop;
              this.toggleBar(!entry.isIntersecting && scrolledPast);
            });
          },
          { rootMargin: `-${this.revealLine}px 0px 0px 0px`, threshold: 0 }
        );

        this.intersectionObserver.observe(anchor);
      }

      toggleBar(show) {
        if (show === this.isVisible) return;
        this.isVisible = show;
        this.classList.toggle('is-visible', show);

        if (show) {
          this.updateTop();
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
        } else {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      }

      onScroll() {
        if (this.topFrame) return;
        this.topFrame = requestAnimationFrame(() => {
          this.topFrame = null;
          this.updateTop();
        });
      }

      // Sit directly beneath the sticky header rather than covering it.
      updateTop() {
        if (!this.header) return;
        const bottom = this.header.getBoundingClientRect().bottom;
        this.style.top = `${Math.max(0, Math.round(bottom))}px`;
      }
    }
  );
}
